import type { Rect } from "@/lib/math/rect";
import type { PrimitiveDefinition } from "./primitives";
import type { NodeStyle } from "./styles";
import type { RichTextModel } from "./text";
import type { VectorNetwork } from "./vectorNetwork";
import type { StyleBindings } from "./documentStyles";
import { DEFAULT_NODE_CONSTRAINTS, normalizeNodeConstraints, type NodeConstraints } from "./constraints";
import type { Guide } from "./guides";

export interface Transform {
  x: number;
  y: number;
  rotation: number;
  scaleX: number;
  scaleY: number;
}

export type AutoLayoutMode = "off" | "flex" | "grid" | "repeat" | "mirror";
export type FlexLayoutDirection = "row" | "row-reverse" | "column" | "column-reverse";
export type FlexLayoutJustify = "flex-start" | "center" | "flex-end" | "space-between" | "space-around" | "space-evenly";
export type FlexLayoutAlign = "auto" | "flex-start" | "center" | "flex-end" | "stretch";

export interface LayoutPadding {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export type FlexAxisSizing = "fixed" | "hug";

export interface FrameFlexLayout {
  direction: FlexLayoutDirection;
  justifyContent: FlexLayoutJustify;
  alignItems: Exclude<FlexLayoutAlign, "auto">;
  gap: number;
  padding: LayoutPadding;
  widthSizing?: FlexAxisSizing;
  heightSizing?: FlexAxisSizing;
}

export type RepeatLayoutMode = "row" | "column" | "grid" | "radial";

export interface FrameRepeatLayout {
  mode: RepeatLayoutMode;
  /** Total instances including the original. */
  count: number;
  /** Columns for grid mode. */
  columns: number;
  /** Rows for grid mode. When > 0, instance count is columns×rows. When 0, count wraps by columns. */
  rows: number;
  gap: number;
  columnGap: number;
  rowGap: number;
  /** Distance from ring center to the prototype content center (radial mode). */
  radius: number;
  /** Angle of the prototype petal in degrees (0 = +X); copies step evenly from here. */
  startAngle: number;
}

export type MirrorLayoutAxis = "horizontal" | "vertical" | "both";

export interface FrameMirrorLayout {
  axis: MirrorLayoutAxis;
  /** Extra space between the original content and its reflection. */
  gap: number;
}

export interface FlexChildLayout {
  grow: number;
  shrink: number;
  alignSelf: FlexLayoutAlign;
  absolute: boolean;
  fillWidth: boolean;
  fillHeight: boolean;
  fixedWidth?: number;
  fixedHeight?: number;
}

export interface BaseNode {
  id: string;
  name: string;
  type: string;
  visible: boolean;
  locked: boolean;
  isMask?: boolean;
  transform: Transform;
  bounds: Rect;
  style: NodeStyle;
  flexChild?: FlexChildLayout;
  constraints?: NodeConstraints;
  componentSourceId?: string;
  styleBindings?: StyleBindings;
  nameManuallyEdited?: boolean;
}

export interface GroupNode extends BaseNode {
  type: "group";
  children: SceneNode[];
}

export interface ComponentNode extends BaseNode {
  type: "component";
  children: ComponentVariantNode[];
}

export interface ComponentVariantNode extends BaseNode {
  type: "componentVariant";
  children: SceneNode[];
}

export interface ComponentInstanceOverride {
  bounds?: Rect;
  transform?: Transform;
  style?: NodeStyle;
  name?: string;
  visible?: boolean;
  locked?: boolean;
  primitive?: PrimitiveDefinition;
  text?: RichTextModel;
  /**
   * Style-library bindings set on the instance child itself. An empty object
   * means the instance explicitly cleared a binding the variant source carries;
   * `undefined` means "inherit the source's bindings".
   */
  styleBindings?: StyleBindings;
}

export interface ComponentInstanceNode extends BaseNode {
  type: "instance";
  componentId: string;
  variantId: string;
  overrides: Record<string, ComponentInstanceOverride>;
  children: SceneNode[];
  /** True when the user has explicitly resized this instance; suppresses automatic size tracking from the variant. */
  explicitBounds?: boolean;
}

export interface FrameNode extends BaseNode {
  type: "frame";
  children: SceneNode[];
  guides?: Guide[];
  cornerRadii?: [number, number, number, number];
  clipsContent: boolean;
  autoLayout?: AutoLayoutMode;
  flexLayout?: FrameFlexLayout;
  repeatLayout?: FrameRepeatLayout;
  mirrorLayout?: FrameMirrorLayout;
  /** Id of a document library script attached to this frame. */
  scriptId?: string;
}

export interface PrimitiveNode extends BaseNode {
  type: "primitive";
  primitive: PrimitiveDefinition;
}

export interface ShapeNode extends BaseNode {
  type: "shape";
  path: string;
}

export type BooleanOperation = "union" | "subtract" | "intersect" | "exclude";

export interface BooleanResolvedPath {
  path: string;
  contours: Array<Array<{ x: number; y: number }>>;
}

export interface BooleanSourceGeometry {
  nodeId: string;
  sourceBounds?: Rect;
  sourceKey?: string;
  bounds: Rect;
  path?: string;
  contours: Array<Array<{ x: number; y: number }>>;
}

export interface BooleanNode extends BaseNode {
  type: "boolean";
  operation: BooleanOperation;
  sourceNodes: SceneNode[];
  sourceGeometry?: BooleanSourceGeometry[];
  resolved: BooleanResolvedPath;
}

export interface VectorNetworkNode extends BaseNode {
  type: "vectorNetwork";
  network: VectorNetwork;
}

export interface TextNode extends BaseNode {
  type: "text";
  text: RichTextModel;
}

export type SceneNode = FrameNode | GroupNode | ComponentNode | ComponentVariantNode | ComponentInstanceNode | PrimitiveNode | ShapeNode | BooleanNode | VectorNetworkNode | TextNode;

export function createIdentityTransform(): Transform {
  return { x: 0, y: 0, rotation: 0, scaleX: 1, scaleY: 1 };
}

export function createDefaultFrameFlexLayout(): FrameFlexLayout {
  return {
    direction: "row",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    gap: 8,
    padding: { top: 16, right: 16, bottom: 16, left: 16 },
    widthSizing: "hug",
    heightSizing: "hug",
  };
}

export function createDefaultFrameRepeatLayout(): FrameRepeatLayout {
  return {
    mode: "row",
    count: 3,
    columns: 3,
    rows: 0,
    gap: 16,
    columnGap: 16,
    rowGap: 16,
    radius: 80,
    startAngle: 0,
  };
}

export function createDefaultFrameMirrorLayout(): FrameMirrorLayout {
  return {
    axis: "horizontal",
    gap: 0,
  };
}

export function normalizeFrameFlexLayout(layout?: Partial<FrameFlexLayout>): FrameFlexLayout {
  const defaults = createDefaultFrameFlexLayout();
  return {
    ...defaults,
    ...layout,
    padding: {
      ...defaults.padding,
      ...layout?.padding,
    },
    widthSizing: layout?.widthSizing ?? defaults.widthSizing,
    heightSizing: layout?.heightSizing ?? defaults.heightSizing,
  };
}

export function normalizeFrameRepeatLayout(layout?: Partial<FrameRepeatLayout>): FrameRepeatLayout {
  const defaults = createDefaultFrameRepeatLayout();
  const count = Math.max(1, Math.round(layout?.count ?? defaults.count));
  const columns = Math.max(1, Math.round(layout?.columns ?? defaults.columns));
  const rows = Math.max(0, Math.round(layout?.rows ?? defaults.rows));
  return {
    mode: layout?.mode ?? defaults.mode,
    count,
    columns,
    rows,
    gap: Math.max(0, layout?.gap ?? defaults.gap),
    columnGap: Math.max(0, layout?.columnGap ?? defaults.columnGap),
    rowGap: Math.max(0, layout?.rowGap ?? defaults.rowGap),
    radius: Math.max(0, layout?.radius ?? defaults.radius),
    startAngle: layout?.startAngle ?? defaults.startAngle,
  };
}

export function normalizeFrameMirrorLayout(layout?: Partial<FrameMirrorLayout>): FrameMirrorLayout {
  const defaults = createDefaultFrameMirrorLayout();
  return {
    axis: layout?.axis ?? defaults.axis,
    gap: Math.max(0, layout?.gap ?? defaults.gap),
  };
}

export function createDefaultFlexChildLayout(): FlexChildLayout {
  return {
    grow: 0,
    shrink: 1,
    alignSelf: "auto",
    absolute: false,
    fillWidth: false,
    fillHeight: false,
  };
}

export function frameAutoLayoutMode(frame: FrameNode): AutoLayoutMode {
  return frame.autoLayout ?? "off";
}

export function frameCornerRadii(frame: FrameNode): [number, number, number, number] {
  return frame.cornerRadii ? [...frame.cornerRadii] as [number, number, number, number] : [0, 0, 0, 0];
}

export function frameFlexLayout(frame: FrameNode): FrameFlexLayout {
  return normalizeFrameFlexLayout(frame.flexLayout);
}

export function frameRepeatLayout(frame: FrameNode): FrameRepeatLayout {
  return normalizeFrameRepeatLayout(frame.repeatLayout);
}

export function frameMirrorLayout(frame: FrameNode): FrameMirrorLayout {
  return normalizeFrameMirrorLayout(frame.mirrorLayout);
}

export function isPatternAutoLayoutMode(mode: AutoLayoutMode): boolean {
  return mode === "repeat" || mode === "mirror";
}

export function flexChildLayout(node: SceneNode): FlexChildLayout {
  return { ...createDefaultFlexChildLayout(), ...node.flexChild };
}

export function nodeConstraints(node: SceneNode): NodeConstraints {
  return normalizeNodeConstraints(node.constraints ?? DEFAULT_NODE_CONSTRAINTS);
}

export type { NodeConstraints, HorizontalConstraint, VerticalConstraint } from "./constraints";
